// frontend/packages/business-console/src/pages/WorkflowFormFill.jsx
//
// Work item 4.12 (lean) — Console-side renderer + submit. Consumes
// the compiled schema from 4.6's GET endpoint and walks the flat
// `fields[]` list, rendering MUI inputs per type. Submit fires
// through the 4.7 POST endpoint, with the 5.2 offline outbox wired
// in so a field rep on a dead Wi-Fi spot doesn't lose work.
//
// Coverage (full v1 — renderer-completeness pass):
//   - Top-level scalar/choice types: TextInput, EmailInput, PhoneInput,
//     Textarea, Number, Checkbox, Select, RadioGroup, DateInput,
//     MultiSelect (checkbox group, value = array), FileUpload (4.8),
//     SignaturePad (5.7), GeoTag (5.6).
//   - Repeatable: rendered as add/remove item rows honoring min/max;
//     value at `values.<name>` is an array of objects keyed by child
//     name (`[{ sku, qty }]`). The compiler emits a container field
//     (`items`, type Repeatable) + child fields (`items[].sku`) which
//     `buildRenderPlan` groups together.
//   - Sections are flattened (no-op in the data model).
//   - Unknown types render as a TextInput with a "fallback" caption.
//   v1 limits: no nested Repeatables (`items[].sub[].x`); a FileUpload
//   inside a Repeatable uploads fine but its ref lives in
//   `values.<name>[].x` rather than the submission's top-level
//   `attachments[]` (so it's outside the scan-status lifecycle).
//   - FileUpload (4.8): renderer requests a SAS via
//     `requestWorkflowFormUploadToken`, PUTs the file directly to
//     Azure Blob via the returned URL, then includes the
//     attachment ref in the submit payload's `attachments[]`. Online
//     only — offline mode disables the upload affordance because we
//     don't have a binary outbox (yet). ClamAV scanStatus stays
//     'pending' until the worker (separate follow-up) flips it.
//
// `/business/:slug/forms/:slug/new`

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox as MuiCheckbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import {
  getWorkflowForm,
  submitWorkflowForm,
  requestWorkflowFormUploadToken,
  uploadBlobResumable,
} from '../services/workflowFormService';
import { reverseGeocode } from '../services/geoService';
import useOfflineOutbox from '../hooks/useOfflineOutbox';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { newClientUuid } from '../lib/outboxStore';
import OfflineStatusChip from '../components/OfflineStatusChip';
import CustomerPicker from './Quotes/CustomerPicker';
// Decision-tool engine (5.14 conditional visibility + 5.15 the calculator).
// This is the SAME safe-expression interpreter the backend recomputes with on
// submit (`@upryzze/forms/expr`) — never eval/new Function. The Console fill
// renderer (THIS file) is the only production renderer; see the boundary note
// at the top of the file and CLAUDE.md.
import { evaluateBoolean, evaluateNumber } from '@upryzze/forms/expr';

const TYPE_DEFAULT_VALUE = {
  TextInput: '',
  EmailInput: '',
  PhoneInput: '',
  Textarea: '',
  Number: '',
  DateInput: '',
  Select: '',
  RadioGroup: '',
  Checkbox: false,
  MultiSelect: [],
  // 4.8 — FileUpload values are arrays of attachment refs ready to
  // include in the submit payload's `attachments[]`.
  FileUpload: [],
  // 5.7 — SignaturePad stores a one-element attachment array so the
  // submit flatten in handleSubmit treats it like FileUpload (the
  // PNG goes through the same SAS upload + scanStatus lifecycle).
  SignaturePad: [],
  // 5.6 — GeoTag stores a plain object once captured, null before.
  // The submit flatten checks Array.isArray + blobUrl shape, so an
  // object value rides through the existing JSON path under values.
  GeoTag: null,
  // 5.15 — Computed ("calculator") starts empty. The renderer SHOWS the live
  // computed value as an editable default; an empty state means "no override,
  // use the recommended value" and the server fills it authoritatively on
  // submit. A non-empty state is the operator's override.
  Computed: '',
};

// A fresh copy of a type's default — arrays must not be shared across
// fields / repeatable rows (mutating one would mutate all).
function defaultFor(type) {
  const d = Object.prototype.hasOwnProperty.call(TYPE_DEFAULT_VALUE, type)
    ? TYPE_DEFAULT_VALUE[type]
    : '';
  return Array.isArray(d) ? [] : d;
}

// Build an ordered render plan from the flat compiled `fields[]`.
// Top-level fields render directly. A `<Repeatable name="items">`
// emits a container field (path `items`, type `Repeatable`) followed
// by its child fields (paths `items[].sku`); we group those children
// under the container so they render + collect as an array of objects
// at `values.items`. Deeper nesting (`items[].sub[].x`) is not
// supported in v1 and is skipped.
function buildRenderPlan(fields) {
  const all = Array.isArray(fields) ? fields : [];
  const plan = [];
  const byGroup = {};
  for (const f of all) {
    if (typeof f.path !== 'string') continue;
    if (f.type === 'Repeatable') {
      const node = { kind: 'repeatable', field: f, children: [] };
      byGroup[f.props?.name || f.path] = node;
      plan.push(node);
      continue;
    }
    const br = f.path.indexOf('[]');
    if (br >= 0) {
      const groupName = f.path.slice(0, br);
      const childName = f.path.slice(br + 3); // strip "[]."
      const node = byGroup[groupName];
      if (
        node &&
        childName &&
        !childName.includes('.') &&
        !childName.includes('[]')
      ) {
        node.children.push({ ...f, childName });
      }
      continue;
    }
    if (f.path.includes('.')) continue; // stray nested path — skip (v1)
    plan.push({ kind: 'field', field: f });
  }
  return plan;
}

// 5.16 — group the flat render plan into ordered sections for the stepper.
// Each plan node's field carries `section: { index, title }` (from the compiler)
// or none. Returns [{ index, title, nodes }] in document/first-appearance order.
// Fields authored outside any <Section> collapse into one leading group.
function groupBySections(renderPlan) {
  const groups = [];
  const byIndex = new Map();
  for (const node of renderPlan) {
    const sec = node.field?.section;
    const idx = sec && Number.isInteger(sec.index) ? sec.index : -1;
    let g = byIndex.get(idx);
    if (!g) {
      g = { index: idx, title: sec?.title || '', nodes: [] };
      byIndex.set(idx, g);
      groups.push(g);
    }
    g.nodes.push(node);
  }
  return groups;
}

function emptyRepeatItem(children) {
  const item = {};
  for (const c of children) item[c.childName] = defaultFor(c.type);
  return item;
}

function initialValuesFromFields(fields) {
  const out = {};
  for (const node of buildRenderPlan(fields)) {
    if (node.kind === 'field') {
      out[node.field.path] = defaultFor(node.field.type);
    } else {
      const key = node.field.props?.name || node.field.path;
      const min = Number.isFinite(node.field.props?.min)
        ? node.field.props.min
        : 0;
      const items = [];
      for (let i = 0; i < min; i += 1)
        items.push(emptyRepeatItem(node.children));
      out[key] = items;
    }
  }
  return out;
}

function readOptions(props) {
  const raw = props?.options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (typeof o === 'string') return { value: o, label: o };
      if (o && typeof o === 'object' && o.value != null) {
        return { value: String(o.value), label: String(o.label ?? o.value) };
      }
      return null;
    })
    .filter(Boolean);
}

// ── Decision-tool evaluation (5.14 + 5.15) ──────────────────────────────
//
// All three of {visibleWhen, Computed, attr()} run through the shared
// `@upryzze/forms/expr` engine, with a context shaped exactly like the one the
// backend builds in conditionalVisibility.ts / computedFields.ts:
//   { values, attr(fieldName, attrName), rows(group) }
// so the client preview and the authoritative server recompute agree.

// Build the evaluation context for the current `values`. `attr(field, name)`
// resolves a catalog attribute by reading the SELECTED option's snapshotted
// `attrs` (the compiler bakes each catalog item's attrs onto the inline
// options at publish — 5.13d). `rows(group)` exposes a Repeatable's rows.
function buildEvalContext(values, fields) {
  const byName = {};
  for (const f of fields || []) {
    const nm = f?.props?.name;
    if (typeof nm === 'string') byName[nm] = f;
  }
  return {
    values: values || {},
    attr: (fieldName, attrName) => {
      const f = byName[fieldName];
      const opts = Array.isArray(f?.props?.options) ? f.props.options : [];
      const selected = values?.[fieldName];
      const opt = opts.find(
        (o) =>
          o && typeof o === 'object' && String(o.value) === String(selected)
      );
      const attrs = opt && typeof opt.attrs === 'object' ? opt.attrs : null;
      if (!attrs) return null;
      return Object.prototype.hasOwnProperty.call(attrs, attrName)
        ? attrs[attrName]
        : null;
    },
    rows: (group) => (Array.isArray(values?.[group]) ? values[group] : []),
  };
}

// Names of top-level fields hidden by their own `visibleWhen` against the
// current values. (Section-level visibleWhen isn't representable in the flat
// compiled `fields[]`; the server walks the tree and is the authority. Author
// `visibleWhen` on the field, not the Section — see the rollout runbook.)
function computeHiddenNames(fields, ctx) {
  const hidden = new Set();
  for (const f of fields || []) {
    const vw = f?.props?.visibleWhen;
    const nm = f?.props?.name;
    if (typeof vw === 'string' && typeof nm === 'string') {
      if (!evaluateBoolean(vw, ctx)) hidden.add(nm);
    }
  }
  return hidden;
}

// Live computed value per Computed field, in schema order so a formula may
// reference an earlier computed field (feed-forward mirrors the backend). When
// the operator hasn't overridden a value, the computed result feeds the next
// formula; once overridden, their value does.
function computeComputedValues(fields, baseCtx) {
  const working = { ...baseCtx.values };
  const ctx = { ...baseCtx, values: working };
  const out = {};
  for (const f of fields || []) {
    if (f?.type !== 'Computed') continue;
    const nm = f.props?.name;
    const formula = f.props?.formula;
    if (typeof nm !== 'string' || typeof formula !== 'string') continue;
    const computed = evaluateNumber(formula, ctx);
    out[nm] = computed;
    const override = baseCtx.values[nm];
    const hasOverride =
      override !== '' && override !== null && override !== undefined;
    working[nm] = hasOverride ? override : computed;
  }
  return out;
}

// Format a computed number for display per the field's `format` + `unit`.
function formatComputed(value, format, unit) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '';
  }
  let s;
  if (format === 'integer') s = String(Math.round(value));
  else if (format === 'currency') s = `$${value.toFixed(2)}`;
  else s = String(value);
  return unit && format !== 'currency' ? `${s} ${unit}` : s;
}

// 5.x — downscale a captured/selected image before upload. Phone cameras
// produce multi-megapixel, multi-MB photos; the service report displays them
// at ≤240px, and a full-res photo bloats the rendered PDF past the sidecar's
// size cap (RENDER_TOO_LARGE). We re-encode to JPEG at a sane max dimension so
// the stored attachment + the embedded PDF image are a few hundred KB, not
// tens of MB. Non-image files (PDFs, etc.) are returned untouched. Failures
// degrade gracefully — the original file is uploaded as-is.
const IMAGE_MAX_DIM = 1600; // longest edge, px
const IMAGE_JPEG_QUALITY = 0.82;

async function downscaleImageFile(file) {
  if (
    !file ||
    typeof file.type !== 'string' ||
    !file.type.startsWith('image/')
  ) {
    return file; // not an image — leave it alone
  }
  // GIFs can be animated; re-encoding would flatten them. Skip (rare here).
  if (file.type === 'image/gif') return file;
  try {
    const bitmap =
      typeof createImageBitmap === 'function'
        ? await createImageBitmap(file)
        : await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
          });
    const srcW = bitmap.width || bitmap.naturalWidth;
    const srcH = bitmap.height || bitmap.naturalHeight;
    if (!srcW || !srcH) return file;
    const scale = Math.min(1, IMAGE_MAX_DIM / Math.max(srcW, srcH));
    // Already small enough AND already a JPEG → no point re-encoding.
    if (scale === 1 && file.type === 'image/jpeg') {
      if (bitmap.close) bitmap.close();
      return file;
    }
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', IMAGE_JPEG_QUALITY)
    );
    if (!blob) return file;
    // If re-encoding somehow produced a larger file, keep the original.
    if (blob.size >= file.size && file.type === 'image/jpeg') return file;
    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file; // any failure → upload the original
  }
}

function FileUploadField({
  field,
  value,
  onChange,
  error,
  formSlug,
  clientUuid,
  online,
}) {
  const props = field.props || {};
  const label = props.label || field.path;
  const accept = typeof props.accept === 'string' ? props.accept : '';
  const multiple = Boolean(props.multiple);
  const maxFiles = Number.isFinite(props.maxFiles) ? props.maxFiles : null;
  const maxSizeMB = Number.isFinite(props.maxSizeMB) ? props.maxSizeMB : null;
  const required = Boolean(props.required);
  const list = Array.isArray(value) ? value : [];

  const [uploading, setUploading] = React.useState(false);
  const [localError, setLocalError] = React.useState(null);

  const helperText =
    error ||
    localError ||
    props.helperText ||
    (!online
      ? 'Attachments are online-only — reconnect to add files.'
      : maxSizeMB
        ? `Max ${maxSizeMB} MB per file.`
        : '');

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    if (files.length === 0) return;
    setLocalError(null);

    if (maxFiles != null && list.length + files.length > maxFiles) {
      setLocalError(`No more than ${maxFiles} file(s) per field.`);
      return;
    }

    setUploading(true);
    try {
      const next = [...list];
      for (const rawFile of files) {
        // Downscale images before anything else so the size cap + upload + the
        // eventual PDF embed all see the small version, not the raw camera file.
        const file = await downscaleImageFile(rawFile);
        if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
          throw new Error(`${file.name} exceeds the ${maxSizeMB} MB limit.`);
        }
        const tokenArgs = {
          fieldId: field.path,
          clientUuid,
          filename: file.name,
          sizeBytes: file.size,
          mimeType: file.type || 'application/octet-stream',
        };
        const token = await requestWorkflowFormUploadToken(formSlug, tokenArgs);
        // 5.3 — chunked + resumable; re-mints a fresh SAS on expiry.
        await uploadBlobResumable(file, {
          token,
          refreshToken: () =>
            requestWorkflowFormUploadToken(formSlug, tokenArgs),
        });
        next.push({
          fieldId: token.fieldId,
          blobUrl: token.blobUrl,
          originalName: token.originalName,
          sizeBytes: token.sizeBytes,
          mimeType: token.mimeType,
          // ClamAV worker (separate follow-up) flips this to
          // 'clean' / 'infected' via the 4.3 repo helper.
          scanStatus: 'pending',
        });
        if (!multiple) break;
      }
      onChange(field.path, next);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Upload failed.';
      setLocalError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(field.path, next);
  };

  return (
    <FormControl error={Boolean(error || localError)}>
      <FormLabel>{label + (required ? ' *' : '')}</FormLabel>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}
      >
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={
            uploading ? (
              <CircularProgress size={14} />
            ) : (
              <AttachFileOutlinedIcon />
            )
          }
          disabled={uploading || !online}
        >
          {uploading ? 'Uploading…' : multiple ? 'Add files' : 'Choose file'}
          <input
            type="file"
            hidden
            accept={accept || undefined}
            multiple={multiple}
            onChange={handlePick}
          />
        </Button>
        {list.map((att, idx) => (
          <Chip
            key={`${att.blobUrl}-${idx}`}
            size="small"
            icon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
            label={`${att.originalName} (${Math.round(att.sizeBytes / 1024)} KB)`}
            onDelete={() => handleRemove(idx)}
            deleteIcon={<CloseRoundedIcon fontSize="small" />}
          />
        ))}
      </Stack>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

// 5.8 — PhotoCapture. A camera/file image field: same SAS-upload lifecycle +
// attachment value shape as FileUpload (so handleSubmit's flatten routes it into
// the top-level `attachments[]` unchanged), but image-only, multi-shot, with a
// thumbnail grid and the mobile rear-camera hint (`capture="environment"`).
// Local object-URL previews are transient UI state — never part of the value.
function PhotoCaptureField({
  field,
  value,
  onChange,
  error,
  formSlug,
  clientUuid,
  online,
}) {
  const props = field.props || {};
  const label = props.label || field.path;
  const required = Boolean(props.required);
  // Sane default cap so a field-tech can't queue hundreds of camera shots.
  const maxFiles = Number.isFinite(props.maxFiles) ? props.maxFiles : 10;
  const maxSizeMB = Number.isFinite(props.maxSizeMB) ? props.maxSizeMB : null;
  const list = Array.isArray(value) ? value : [];

  const [uploading, setUploading] = React.useState(false);
  const [localError, setLocalError] = React.useState(null);
  // Transient previews (object URLs) keyed by blobUrl — UI only, not submitted.
  const [previews, setPreviews] = React.useState({});

  React.useEffect(
    () => () => {
      Object.values(previews).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* noop */
        }
      });
    },
    [previews]
  );

  const atMax = maxFiles != null && list.length >= maxFiles;
  const helperText =
    error ||
    localError ||
    props.helperText ||
    (!online
      ? 'Photos are online-only — reconnect to add.'
      : maxFiles
        ? `Up to ${maxFiles} photo(s).`
        : '');

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setLocalError(null);
    if (maxFiles != null && list.length + files.length > maxFiles) {
      setLocalError(`No more than ${maxFiles} photo(s).`);
      return;
    }
    setUploading(true);
    try {
      const next = [...list];
      const nextPreviews = { ...previews };
      for (const rawFile of files) {
        const file = await downscaleImageFile(rawFile);
        if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
          throw new Error(`Photo exceeds the ${maxSizeMB} MB limit.`);
        }
        const tokenArgs = {
          fieldId: field.path,
          clientUuid,
          filename: file.name,
          sizeBytes: file.size,
          mimeType: file.type || 'image/jpeg',
        };
        const token = await requestWorkflowFormUploadToken(formSlug, tokenArgs);
        // 5.3 — chunked + resumable; re-mints a fresh SAS on expiry.
        await uploadBlobResumable(file, {
          token,
          refreshToken: () =>
            requestWorkflowFormUploadToken(formSlug, tokenArgs),
        });
        try {
          nextPreviews[token.blobUrl] = URL.createObjectURL(file);
        } catch {
          /* preview is best-effort */
        }
        next.push({
          fieldId: token.fieldId,
          blobUrl: token.blobUrl,
          originalName: token.originalName,
          sizeBytes: token.sizeBytes,
          mimeType: token.mimeType,
          scanStatus: 'pending',
        });
      }
      setPreviews(nextPreviews);
      onChange(field.path, next);
    } catch (err) {
      setLocalError(
        err?.response?.data?.message || err?.message || 'Upload failed.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (idx) => {
    const removed = list[idx];
    const next = list.filter((_, i) => i !== idx);
    if (removed && previews[removed.blobUrl]) {
      try {
        URL.revokeObjectURL(previews[removed.blobUrl]);
      } catch {
        /* noop */
      }
      const np = { ...previews };
      delete np[removed.blobUrl];
      setPreviews(np);
    }
    onChange(field.path, next);
  };

  return (
    <FormControl error={Boolean(error || localError)}>
      <FormLabel>{label + (required ? ' *' : '')}</FormLabel>
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', gap: 1, mt: 1, alignItems: 'center' }}
      >
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={
            uploading ? (
              <CircularProgress size={14} />
            ) : (
              <PhotoCameraOutlinedIcon />
            )
          }
          disabled={uploading || !online || atMax}
        >
          {uploading ? 'Uploading…' : 'Add photo'}
          <input
            type="file"
            hidden
            accept="image/*"
            capture="environment"
            multiple
            onChange={handlePick}
          />
        </Button>
        {list.map((att, idx) => (
          <Box
            key={`${att.blobUrl}-${idx}`}
            sx={{
              position: 'relative',
              width: 64,
              height: 64,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {previews[att.blobUrl] ? (
              <Box
                component="img"
                src={previews[att.blobUrl]}
                alt={att.originalName || 'photo'}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <ImageOutlinedIcon fontSize="small" />
              </Box>
            )}
            <IconButton
              size="small"
              aria-label="Remove photo"
              onClick={() => handleRemove(idx)}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                p: 0.25,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Stack>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

// 5.7 — signature widget. Two modes:
//   - Draw: pointer events (touch + mouse + stylus) into a 2D canvas.
//   - Type: the signer types their name, rendered in a script font.
// Either way the canvas is exported as a PNG and uploaded via the 4.8 SAS
// path; the value is a one-element attachment array so handleSubmit's flatten
// routes it into the top-level `attachments[]` with `fieldId === field.path`.
//
// Capture is AUTOMATIC (debounced) — there is no manual "save" step, so a
// drawn/typed signature can't be silently lost on submit (the old Save-button
// trap). `required` is honored by the parent's validation; an optional
// signature simply submits empty when untouched.
function SignaturePadField({
  field,
  value,
  onChange,
  error,
  formSlug,
  clientUuid,
  online,
}) {
  const props = field.props || {};
  const label = props.label || field.path;
  const required = Boolean(props.required);
  const penColor =
    typeof props.penColor === 'string' && props.penColor
      ? props.penColor
      : '#111';
  const heightPx = Number.isFinite(props.heightPx) ? props.heightPx : 180;

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const dirtyRef = useRef(false);
  const commitTimerRef = useRef(null);
  // Bumped on every fresh stroke/keystroke; the debounced upload tags the
  // attachment it produced with the run it belongs to, so a stale in-flight
  // upload that resolves late can't clobber a newer capture.
  const captureRunRef = useRef(0);

  const [mode, setMode] = useState('draw'); // 'draw' | 'type'
  const [typed, setTyped] = useState('');
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [dirty, setDirty] = useState(false);

  const list = Array.isArray(value) ? value : [];
  const captured = list.length > 0;

  const statusText = uploading
    ? 'Capturing signature…'
    : captured
      ? 'Signature captured.'
      : '';
  const helperText =
    error ||
    localError ||
    statusText ||
    props.helperText ||
    (!online
      ? 'Signature capture is online-only — reconnect to sign.'
      : mode === 'type'
        ? 'Type your full name; it will be captured as your signature.'
        : 'Sign with your finger, stylus, or mouse.');

  const getCtx = () => canvasRef.current?.getContext('2d') || null;

  // Match the canvas backing store to its CSS box (crisp on hi-DPI). The
  // context is scaled by devicePixelRatio so all drawing uses CSS px coords.
  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = penColor;
  }, [penColor]);

  useEffect(() => {
    sizeCanvas();
  }, [sizeCanvas, heightPx, mode]);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    []
  );

  // Encode the current canvas to a PNG, upload it, and store the attachment.
  const commit = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !dirtyRef.current) return;
    if (!online) {
      setLocalError('Signature capture is online-only — reconnect to sign.');
      return;
    }
    const run = captureRunRef.current;
    setLocalError(null);
    setUploading(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Could not encode signature.'));
        }, 'image/png');
      });
      const filename = `signature-${field.path}-${Date.now()}.png`;
      const tokenArgs = {
        fieldId: field.path,
        clientUuid,
        filename,
        sizeBytes: blob.size,
        mimeType: 'image/png',
      };
      const token = await requestWorkflowFormUploadToken(formSlug, tokenArgs);
      // 5.3 — chunked + resumable; re-mints a fresh SAS on expiry.
      await uploadBlobResumable(blob, {
        token,
        refreshToken: () => requestWorkflowFormUploadToken(formSlug, tokenArgs),
      });
      // A newer stroke/keystroke started while this upload was in flight —
      // drop this result so we don't overwrite the newer capture.
      if (run !== captureRunRef.current) return;
      onChange(field.path, [
        {
          fieldId: token.fieldId,
          blobUrl: token.blobUrl,
          originalName: token.originalName,
          sizeBytes: token.sizeBytes,
          mimeType: token.mimeType,
          scanStatus: 'pending',
        },
      ]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Could not capture signature.';
      setLocalError(msg);
    } finally {
      setUploading(false);
    }
  }, [online, field.path, formSlug, clientUuid, onChange]);

  // Debounce capture so we upload once the signer pauses, not mid-stroke.
  const scheduleCommit = useCallback(() => {
    captureRunRef.current += 1;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      void commit();
    }, 600);
  }, [commit]);

  // ── draw mode ──
  const localPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !online) return;
    e.preventDefault();
    try {
      canvas.setPointerCapture?.(e.pointerId);
    } catch {
      // some browsers throw for unsupported pointer types
    }
    drawingRef.current = true;
    lastPointRef.current = localPoint(e);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const ctx = getCtx();
    const next = localPoint(e);
    const prev = lastPointRef.current;
    if (!ctx || !next || !prev) return;
    e.preventDefault();
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    lastPointRef.current = next;
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setDirty(true);
    }
  };

  const endStroke = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    try {
      canvasRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    if (dirtyRef.current) scheduleCommit(); // auto-capture on stroke end
  };

  // ── type mode ──
  const renderTyped = useCallback(
    (text) => {
      const canvas = canvasRef.current;
      const ctx = getCtx();
      if (!canvas || !ctx) return;
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      const trimmed = text.trim();
      if (!trimmed) {
        dirtyRef.current = false;
        setDirty(false);
        return;
      }
      const fontPx = Math.min(56, Math.max(28, Math.floor(rect.height * 0.4)));
      ctx.fillStyle = penColor;
      ctx.font = `italic ${fontPx}px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(trimmed, rect.width / 2, rect.height / 2);
      dirtyRef.current = true;
      setDirty(true);
    },
    [penColor]
  );

  const handleTypedChange = (e) => {
    const next = e.target.value;
    setTyped(next);
    renderTyped(next);
    if (next.trim()) scheduleCommit();
    else onChange(field.path, []); // emptied → clear the captured value
  };

  // ── shared ──
  const clearAll = () => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    captureRunRef.current += 1; // invalidate any in-flight upload
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (canvas && ctx) {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
    dirtyRef.current = false;
    setDirty(false);
    setTyped('');
    setLocalError(null);
    if (captured) onChange(field.path, []);
  };

  const switchMode = (next) => {
    if (next === mode) return;
    clearAll();
    setMode(next);
  };

  return (
    <FormControl error={Boolean(error || localError)} fullWidth>
      <FormLabel>{label + (required ? ' *' : '')}</FormLabel>

      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
        <Button
          size="small"
          variant={mode === 'draw' ? 'contained' : 'outlined'}
          onClick={() => switchMode('draw')}
        >
          Draw
        </Button>
        <Button
          size="small"
          variant={mode === 'type' ? 'contained' : 'outlined'}
          onClick={() => switchMode('type')}
        >
          Type
        </Button>
      </Stack>

      {mode === 'type' ? (
        <TextField
          fullWidth
          size="small"
          label="Type your name"
          value={typed}
          onChange={handleTypedChange}
          disabled={!online}
          sx={{ mb: 1 }}
        />
      ) : null}

      <Box
        sx={{
          border: (theme) =>
            `1px ${error || localError ? 'solid' : 'dashed'} ${
              error || localError
                ? theme.palette.error.main
                : theme.palette.divider
            }`,
          borderRadius: 1,
          bgcolor: 'background.paper',
          height: heightPx,
          width: '100%',
          position: 'relative',
          touchAction: 'none',
          cursor: mode === 'draw' && online ? 'crosshair' : 'default',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            pointerEvents: mode === 'draw' && online ? 'auto' : 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
        />
        {captured ? (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              pointerEvents: 'none',
            }}
          >
            <Chip
              size="small"
              icon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
              label={uploading ? 'Saving…' : 'Captured'}
              color="success"
              variant="outlined"
            />
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
        <Button
          size="small"
          color="inherit"
          onClick={clearAll}
          disabled={uploading || (!captured && !dirty)}
        >
          Clear
        </Button>
        {uploading ? <CircularProgress size={16} /> : null}
      </Stack>

      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

// 5.6 — GeoTag widget. Calls navigator.geolocation.getCurrentPosition
// on demand; stores `{ lat, lng, accuracyM, capturedAt }` in
// values.<name>. v1 doesn't reverse-geocode — the Google Maps API
// key isn't wired through to Console yet; "Open in Maps" is a static
// `https://www.google.com/maps?q=lat,lng` link that needs no key.
// Works offline (the Geolocation API itself doesn't require a
// network round-trip for the device's GPS fix). The roadmap brief
// (§11) explicitly accepts "no reverse geocode" as the offline /
// no-key path.
function GeoTagField({ field, value, onChange, error }) {
  const props = field.props || {};
  const label = props.label || field.path;
  const required = Boolean(props.required);

  const [capturing, setCapturing] = useState(false);
  const [localError, setLocalError] = useState(null);

  const captured =
    value && typeof value === 'object' && typeof value.lat === 'number';

  const supported =
    typeof navigator !== 'undefined' &&
    typeof navigator.geolocation === 'object' &&
    typeof navigator.geolocation.getCurrentPosition === 'function';

  const helperText =
    error ||
    localError ||
    props.helperText ||
    (!supported
      ? 'Geolocation is not available in this browser.'
      : captured
        ? `Captured ${new Date(value.capturedAt).toLocaleString()}`
        : 'Tap Capture to record your current location.');

  const handleCapture = () => {
    setLocalError(null);
    if (!supported) return;
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const base = {
          lat,
          lng,
          accuracyM:
            typeof pos.coords.accuracy === 'number'
              ? Math.round(pos.coords.accuracy)
              : null,
          address: null,
          capturedAt: new Date().toISOString(),
        };
        // Stamp the value immediately so the operator sees the chip
        // even if the reverse-geocode round trip is slow or fails.
        // The `address` upgrades in-place once the call returns.
        onChange(field.path, base);
        setCapturing(false);
        // Fire-and-forget address enrichment. Never blocks save;
        // failures keep address: null (graceful degrade matches the
        // "Open in Maps" link fallback).
        try {
          const { address } = await reverseGeocode(lat, lng);
          if (address) onChange(field.path, { ...base, address });
        } catch {
          // swallowed — service helper already returns null on error
        }
      },
      (err) => {
        setLocalError(
          err?.code === 1
            ? 'Permission denied — enable location for this site.'
            : err?.code === 3
              ? 'Timed out fetching location.'
              : err?.message || 'Could not get location.'
        );
        setCapturing(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  const handleClear = () => {
    setLocalError(null);
    onChange(field.path, null);
  };

  return (
    <FormControl error={Boolean(error || localError)} fullWidth>
      <FormLabel>{label + (required ? ' *' : '')}</FormLabel>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleCapture}
          disabled={capturing || !supported || captured}
          startIcon={capturing ? <CircularProgress size={14} /> : null}
        >
          {capturing ? 'Locating…' : captured ? 'Captured' : 'Capture location'}
        </Button>
        {captured ? (
          <>
            <Chip
              size="small"
              icon={<CheckCircleOutlineRoundedIcon fontSize="small" />}
              label={
                value.address
                  ? value.address
                  : `${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}${
                      value.accuracyM != null ? ` (±${value.accuracyM}m)` : ''
                    }`
              }
              color="success"
              variant="outlined"
            />
            <Button
              size="small"
              component="a"
              href={`https://www.google.com/maps?q=${value.lat},${value.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Maps
            </Button>
            <Button size="small" color="inherit" onClick={handleClear}>
              Clear
            </Button>
          </>
        ) : null}
      </Stack>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

// Renders a `<Repeatable>` group — an array of item rows, each
// holding the repeatable's child fields. Value at `values.<name>` is
// an array of objects keyed by child name (e.g. `[{ sku, qty }]`).
// add/remove honor the compiled `min`/`max`. Nested FileUpload inside
// a repeatable uploads fine but its ref lives in `values.<name>[].x`
// rather than the submission's top-level `attachments[]` (v1 limit).
function RepeatableField({
  field,
  childFields,
  value,
  onChange,
  error,
  formSlug,
  clientUuid,
  online,
}) {
  const props = field.props || {};
  const label = props.label || props.name || field.path;
  const key = props.name || field.path;
  const min = Number.isFinite(props.min) ? props.min : 0;
  const max = Number.isFinite(props.max) ? props.max : Infinity;
  const items = Array.isArray(value) ? value : [];

  const setItems = (next) => onChange(key, next);
  const addItem = () => {
    if (items.length < max) setItems([...items, emptyRepeatItem(childFields)]);
  };
  const removeItem = (idx) => {
    if (items.length > min) setItems(items.filter((_, i) => i !== idx));
  };
  const changeChild = (idx, childName, v) => {
    setItems(
      items.map((it, i) => (i === idx ? { ...it, [childName]: v } : it))
    );
  };

  return (
    <FormControl
      error={Boolean(error)}
      component="fieldset"
      variant="standard"
      sx={{ display: 'flex' }}
    >
      <FormLabel component="legend">{label}</FormLabel>
      <Stack gap={1.5} sx={{ mt: 1 }}>
        {items.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {label} #{idx + 1}
              </Typography>
              <Tooltip
                title={
                  items.length <= min ? `At least ${min} required` : 'Remove'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => removeItem(idx)}
                    disabled={items.length <= min}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack gap={2}>
              {childFields.map((child) => (
                <FieldRenderer
                  key={child.path}
                  field={child}
                  value={item[child.childName]}
                  onChange={(_path, v) => changeChild(idx, child.childName, v)}
                  formSlug={formSlug}
                  clientUuid={clientUuid}
                  online={online}
                />
              ))}
            </Stack>
          </Paper>
        ))}
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddOutlinedIcon fontSize="small" />}
            onClick={addItem}
            disabled={items.length >= max}
          >
            {props.addLabel || `Add ${label}`}
          </Button>
        </Box>
        {error ? <FormHelperText error>{error}</FormHelperText> : null}
      </Stack>
    </FormControl>
  );
}

function FieldRenderer({
  field,
  value,
  onChange,
  error,
  formSlug,
  clientUuid,
  online,
  computed,
}) {
  const props = field.props || {};
  const label = props.label || field.path;
  const helperText = error || props.helperText || '';
  const required = Boolean(props.required);
  const disabled = false;

  const handle = (v) => onChange(field.path, v);

  switch (field.type) {
    // 5.15 — the calculator. Shows the live computed value (recomputed by the
    // parent from current values via `@upryzze/forms/expr`) as an editable
    // default. Empty state = "use the recommended value": the server fills it
    // authoritatively on submit. A typed value is the operator's override; the
    // report annotates it as "<value> (recommended <computed>)".
    case 'Computed': {
      const override = value ?? '';
      const hasOverride =
        override !== '' && override !== null && override !== undefined;
      const recommended =
        computed === null || computed === undefined
          ? ''
          : formatComputed(computed, props.format, props.unit);
      const display = hasOverride
        ? override
        : computed === null || computed === undefined
          ? ''
          : String(computed);
      const editable = props.editable !== false;
      const hint =
        error ||
        [
          recommended ? `Recommended: ${recommended}` : null,
          props.helperText || null,
        ]
          .filter(Boolean)
          .join(' · ');
      return (
        <TextField
          fullWidth
          type="number"
          label={label + (props.unit ? ` (${props.unit})` : '')}
          required={required}
          helperText={hint}
          error={Boolean(error)}
          value={display}
          onChange={editable ? (e) => handle(e.target.value) : undefined}
          disabled={disabled}
          InputProps={{ readOnly: !editable }}
        />
      );
    }
    case 'Textarea':
      return (
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
          inputProps={
            props.maxLength ? { maxLength: props.maxLength } : undefined
          }
        />
      );
    case 'Number':
      return (
        <TextField
          fullWidth
          type="number"
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
        />
      );
    case 'DateInput':
      return (
        <TextField
          fullWidth
          type="date"
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
      );
    case 'Checkbox':
      return (
        <FormControl error={Boolean(error)}>
          <FormControlLabel
            control={
              <MuiCheckbox
                checked={Boolean(value)}
                onChange={(e) => handle(e.target.checked)}
                disabled={disabled}
              />
            }
            label={label + (required ? ' *' : '')}
          />
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      );
    case 'Select': {
      const opts = readOptions(props);
      return (
        <TextField
          select
          fullWidth
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
        >
          {opts.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    case 'RadioGroup': {
      const opts = readOptions(props);
      return (
        <FormControl error={Boolean(error)}>
          <FormLabel>{label + (required ? ' *' : '')}</FormLabel>
          <RadioGroup value={value ?? ''} onChange={(_e, v) => handle(v)}>
            {opts.map((o) => (
              <FormControlLabel
                key={o.value}
                value={o.value}
                control={<Radio size="small" />}
                label={o.label}
              />
            ))}
          </RadioGroup>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      );
    }
    case 'MultiSelect': {
      const opts = readOptions(props);
      const selected = Array.isArray(value) ? value : [];
      const toggle = (val) => {
        handle(
          selected.includes(val)
            ? selected.filter((x) => x !== val)
            : [...selected, val]
        );
      };
      const atMax = Number.isFinite(props.max) && selected.length >= props.max;
      return (
        <FormControl
          error={Boolean(error)}
          component="fieldset"
          variant="standard"
        >
          <FormLabel component="legend">
            {label + (required ? ' *' : '')}
          </FormLabel>
          <Stack>
            {opts.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <FormControlLabel
                  key={o.value}
                  control={
                    <MuiCheckbox
                      size="small"
                      checked={checked}
                      disabled={disabled || (!checked && atMax)}
                      onChange={() => toggle(o.value)}
                    />
                  }
                  label={o.label}
                />
              );
            })}
          </Stack>
          {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
      );
    }
    case 'EmailInput':
      return (
        <TextField
          fullWidth
          type="email"
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
        />
      );
    case 'PhoneInput':
      return (
        <TextField
          fullWidth
          type="tel"
          label={label}
          required={required}
          helperText={helperText}
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
        />
      );
    case 'FileUpload':
      return (
        <FileUploadField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          formSlug={formSlug}
          clientUuid={clientUuid}
          online={online}
        />
      );
    case 'PhotoCapture':
      return (
        <PhotoCaptureField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          formSlug={formSlug}
          clientUuid={clientUuid}
          online={online}
        />
      );
    case 'SignaturePad':
      return (
        <SignaturePadField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          formSlug={formSlug}
          clientUuid={clientUuid}
          online={online}
        />
      );
    case 'GeoTag':
      return (
        <GeoTagField
          field={field}
          value={value}
          onChange={onChange}
          error={error}
        />
      );
    case 'TextInput':
    default:
      return (
        <TextField
          fullWidth
          label={label}
          required={required}
          helperText={
            helperText ||
            (field.type !== 'TextInput'
              ? `(rendering ${field.type} as text — type not yet wired)`
              : '')
          }
          error={Boolean(error)}
          value={value ?? ''}
          onChange={(e) => handle(e.target.value)}
          disabled={disabled}
          inputProps={
            props.maxLength ? { maxLength: props.maxLength } : undefined
          }
        />
      );
  }
}

function validateLocally(fields, values, hidden) {
  const errors = {};
  for (const f of fields || []) {
    if (typeof f.path !== 'string') continue;
    if (f.path.includes('[]') || f.path.includes('.')) continue;
    // 5.15 — Computed fields are filled/recomputed by the server; never block.
    if (f.type === 'Computed') continue;
    // 5.14 — a field hidden by `visibleWhen` skips its `required` check (the
    // server independently does the same on submit).
    const nm = f.props?.name;
    if (hidden && typeof nm === 'string' && hidden.has(nm)) continue;
    if (!f.props?.required) continue;
    const v = values[f.path];
    if (
      v === undefined ||
      v === null ||
      v === '' ||
      (Array.isArray(v) && v.length === 0)
    ) {
      errors[f.path] = 'Required';
    }
  }
  return errors;
}

export default function WorkflowFormFill() {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const online = useOnlineStatus();

  // When the form is launched from a customer record, the customer's
  // id (and a display name, for the banner only) ride in the query
  // string. The id flows into the submit payload so the submission is
  // linked to the customer; we return to that customer's profile after
  // submit instead of the global forms list.
  const customerId = searchParams.get('customerId') || null;
  const customerName = searchParams.get('customerName') || null;
  const returnTo = customerId
    ? `${basePath}/crm/${customerId}`
    : `${basePath}/forms`;

  // `clientUuid` is the stable per-editor-session id carried in the payload —
  // the Mongo unique-index layer dedups on it, so a true accidental
  // double-submit of the SAME body is still caught.
  //
  // The request-level Idempotency-Key (Redis layer) is generated PER SUBMIT
  // ATTEMPT, not once per session. The Redis middleware rejects a reused key
  // whose body differs ("Idempotency-Key was used for a request with a
  // different body") — which is exactly what happened when a first submit
  // failed validation and the operator fixed a field and resubmitted. A fresh
  // key per attempt lets a corrected resubmit through while the payload's
  // clientUuid still guards against real duplicates.
  const clientUuidRef = useRef(newClientUuid());
  const idempotencyKeyRef = useRef(newClientUuid());

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [toast, setToast] = useState(null);
  // D22 — a submission must be tied to a customer. When the form is launched
  // from a customer record the id rides in the query string; when launched
  // from the Create hub there's none, so a step-0 picker collects one.
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const effectiveCustomerId = customerId || pickedCustomer?._id || null;

  const outbox = useOfflineOutbox({
    kind: 'workflow-form',
    sync: async (item) => {
      const { formIdOrSlug, ...rest } = item.payload;
      const res = await submitWorkflowForm(formIdOrSlug, rest, {
        idempotencyKey: item.clientUuid,
      });
      return {
        serverId: res?.submission?._id || null,
        serverNumber: null,
      };
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getWorkflowForm(slug);
        if (cancelled) return;
        setForm(res.form);
        setValues(initialValuesFromFields(res.form?.compiledSchema?.fields));
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err?.response?.data?.message ||
              err?.message ||
              "Couldn't load this form."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Ordered render plan: top-level fields + grouped repeatables.
  const renderPlan = useMemo(
    () => buildRenderPlan(form?.compiledSchema?.fields || []),
    [form]
  );
  // Flat list of the top-level compiled fields (for presence
  // validation + the empty-form check). Repeatable child paths are
  // validated structurally by the renderer (min rows), not here.
  const fields = useMemo(() => renderPlan.map((n) => n.field), [renderPlan]);

  // 5.14 + 5.15 — recompute the decision-tool state whenever `values` change.
  // `evalContext` feeds the shared expression engine; `hiddenNames` drives
  // conditional show/hide + required-skip; `computedValues` are the live
  // calculator results shown as editable defaults. The server independently
  // recomputes all of this on submit (it's the authority); this is the live
  // preview so the operator sees the form react as they fill it.
  const evalContext = useMemo(
    () => buildEvalContext(values, fields),
    [values, fields]
  );
  const hiddenNames = useMemo(
    () => computeHiddenNames(fields, evalContext),
    [fields, evalContext]
  );
  const computedValues = useMemo(
    () => computeComputedValues(fields, evalContext),
    [fields, evalContext]
  );

  // 5.16 — responsive section stepper. Sections come from the compiler
  // (`field.section`). On phones we default to a one-section-at-a-time wizard;
  // on desktop, the single scroll. On small screens the user can toggle to the
  // full form (founder request).
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const sectionGroups = useMemo(
    () => groupBySections(renderPlan),
    [renderPlan]
  );
  const multiSection = sectionGroups.length > 1;
  const [showFullForm, setShowFullForm] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const stepped = multiSection && isSmallScreen && !showFullForm;

  // Keep activeStep in range if the section count changes.
  useEffect(() => {
    if (activeStep > sectionGroups.length - 1) {
      setActiveStep(Math.max(0, sectionGroups.length - 1));
    }
  }, [sectionGroups.length, activeStep]);

  // On a failed submit, jump the wizard to the first section with an error.
  useEffect(() => {
    if (!stepped) return;
    if (Object.keys(fieldErrors).length === 0) return;
    const idx = sectionGroups.findIndex((g) =>
      g.nodes.some((n) => {
        const nm = n.field.props?.name || n.field.path;
        return (
          Object.prototype.hasOwnProperty.call(fieldErrors, n.field.path) ||
          Object.prototype.hasOwnProperty.call(fieldErrors, nm)
        );
      })
    );
    if (idx >= 0) setActiveStep(idx);
  }, [fieldErrors, sectionGroups, stepped]);

  const handleFieldChange = (path, v) => {
    setValues((prev) => ({ ...prev, [path]: v }));
    if (fieldErrors[path]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form) return;
    const errs = validateLocally(
      form.compiledSchema?.fields,
      values,
      hiddenNames
    );
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSubmitError('Please fix the highlighted fields.');
      return;
    }
    if (!effectiveCustomerId) {
      setSubmitError('Choose a customer for this form before submitting.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    // Fresh Idempotency-Key for THIS attempt — a corrected resubmit (different
    // body) must not collide with a prior failed attempt's key. The payload's
    // stable clientUuid still guards true duplicates at the Mongo layer.
    idempotencyKeyRef.current = newClientUuid();
    // 4.8 — flatten FileUpload field values into the top-level
    // `attachments[]` shape the server expects. The renderer keeps
    // attachments per-field for the UI; the submit body is one flat
    // list keyed by `fieldId` so the worker can route scan results
    // back. Strip the file values out of `values` so the JSON
    // payload doesn't double-carry the attachment metadata.
    const submitValues = {};
    const attachments = [];
    for (const [path, v] of Object.entries(values)) {
      if (
        Array.isArray(v) &&
        v.length > 0 &&
        v[0] &&
        typeof v[0] === 'object' &&
        typeof v[0].blobUrl === 'string'
      ) {
        for (const att of v) attachments.push(att);
      } else {
        submitValues[path] = v;
      }
    }
    const payload = {
      formIdOrSlug: form.slug,
      clientUuid: clientUuidRef.current,
      values: submitValues,
      attachments,
      status: 'submitted',
      customerId: effectiveCustomerId,
    };
    try {
      if (!online) {
        // Offline — queue and bail.
        await outbox.enqueue({
          clientUuid: clientUuidRef.current,
          payload,
          route: `POST /v2/workflow-forms/${form.slug}/submit`,
        });
        setToast({
          severity: 'warning',
          text: "Saved locally. We'll submit as soon as you're back online.",
        });
        navigate(returnTo);
        return;
      }
      // Online — submit directly. Outbox falls back on network errors.
      try {
        await submitWorkflowForm(form.slug, payload, {
          idempotencyKey: idempotencyKeyRef.current,
        });
        setToast({ severity: 'success', text: `Submitted ${form.name}.` });
        navigate(returnTo);
      } catch (err) {
        const httpStatus = err?.response?.status;
        const isTransient =
          !httpStatus ||
          httpStatus >= 500 ||
          err?.code === 'ERR_NETWORK' ||
          err?.message === 'Network Error';
        if (isTransient) {
          await outbox.enqueue({
            clientUuid: clientUuidRef.current,
            payload,
            route: `POST /v2/workflow-forms/${form.slug}/submit`,
          });
          setToast({
            severity: 'warning',
            text: "Couldn't reach the server. Saved locally; we'll retry on reconnect.",
          });
          navigate(returnTo);
          return;
        }
        // 422 validation: surface server-side issues by path
        if (httpStatus === 422 && Array.isArray(err?.response?.data?.issues)) {
          const next = {};
          for (const iss of err.response.data.issues) {
            next[iss.path] = iss.message || 'Invalid';
          }
          setFieldErrors(next);
          setSubmitError('Please fix the highlighted fields.');
          return;
        }
        setSubmitError(
          err?.response?.data?.message ||
            err?.message ||
            "Couldn't submit form."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Tooltip title="Back to forms">
          <IconButton
            size="small"
            component={RouterLink}
            to={`${basePath}/forms`}
            sx={{ mb: 2 }}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }
  if (!form) return null;

  // Render one plan node (field or repeatable). Returns null for fields hidden
  // by conditional visibility (5.14). Shared by the full + stepped layouts.
  const renderNode = (node) => {
    const nodeName = node.field.props?.name || node.field.path;
    if (hiddenNames.has(nodeName)) return null;
    if (node.kind === 'repeatable') {
      return (
        <RepeatableField
          key={node.field.path}
          field={node.field}
          childFields={node.children}
          value={values[nodeName]}
          error={fieldErrors[nodeName]}
          onChange={handleFieldChange}
          formSlug={form.slug}
          clientUuid={clientUuidRef.current}
          online={online}
        />
      );
    }
    const f = node.field;
    return (
      <FieldRenderer
        key={f.path}
        field={f}
        value={values[f.path]}
        error={fieldErrors[f.path]}
        onChange={handleFieldChange}
        formSlug={form.slug}
        clientUuid={clientUuidRef.current}
        online={online}
        computed={
          f.type === 'Computed' ? computedValues[f.props?.name] : undefined
        }
      />
    );
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 720,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Tooltip title={customerId ? 'Back to customer' : 'Back to forms'}>
          <IconButton
            size="small"
            component={RouterLink}
            to={returnTo}
            sx={{ color: 'text.secondary' }}
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{form.name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            v{form.version} · <Box component="code">{form.slug}</Box>
          </Typography>
        </Box>
        <OfflineStatusChip
          online={online}
          isFlushing={outbox.isFlushing}
          pendingCount={
            outbox.items.filter(
              (it) => it.status === 'pending' || it.status === 'syncing'
            ).length
          }
          failedCount={
            outbox.items.filter((it) => it.status === 'failed').length
          }
          lastSavedAt={outbox.items[0]?.savedAt || null}
        />
      </Stack>

      {customerId ? (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          Saving to the record for{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {customerName || 'the selected customer'}
          </Box>
          .
        </Alert>
      ) : (
        // D22 step-0 — a customer is required before this form can be filed.
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Customer *
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
          >
            Choose who this form is for. Required.
          </Typography>
          <CustomerPicker value={pickedCustomer} onChange={setPickedCustomer} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        {/* 5.16 — small-screen toggle between the stepped wizard and the full
            scrolling form (only when the form actually has multiple sections). */}
        {multiSection && isSmallScreen ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowFullForm((s) => !s)}
              sx={{ textTransform: 'none' }}
            >
              {showFullForm ? 'Stepped view' : 'View full form'}
            </Button>
          </Box>
        ) : null}

        {renderPlan.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>
            This form has no fillable fields.
          </Typography>
        ) : stepped ? (
          // Wizard: one section per screen + progress.
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Step {activeStep + 1} of {sectionGroups.length}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={((activeStep + 1) / sectionGroups.length) * 100}
                sx={{ borderRadius: 1, mt: 0.5 }}
              />
              {sectionGroups[activeStep]?.title ? (
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 1.5 }}>
                  {sectionGroups[activeStep].title}
                </Typography>
              ) : null}
            </Box>
            <Stack spacing={2}>
              {sectionGroups[activeStep].nodes.map(renderNode)}
            </Stack>
          </Stack>
        ) : (
          // Full scroll: today's behavior + section headers when present.
          <Stack spacing={2}>
            {sectionGroups.map((g, gi) => (
              <Stack key={g.index} spacing={2}>
                {g.title ? (
                  <Box sx={{ mt: gi > 0 ? 1 : 0 }}>
                    {gi > 0 ? <Divider sx={{ mb: 1.5 }} /> : null}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {g.title}
                    </Typography>
                  </Box>
                ) : null}
                {g.nodes.map(renderNode)}
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {submitError ? <Alert severity="error">{submitError}</Alert> : null}

      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Button
          component={RouterLink}
          to={`${basePath}/forms`}
          disabled={submitting}
        >
          Cancel
        </Button>
        {stepped ? (
          <Stack direction="row" spacing={1}>
            <Button
              type="button"
              onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              disabled={submitting || activeStep === 0}
              startIcon={<ArrowBackRoundedIcon />}
            >
              Back
            </Button>
            {activeStep < sectionGroups.length - 1 ? (
              <Button
                type="button"
                variant="contained"
                onClick={() =>
                  setActiveStep((s) =>
                    Math.min(sectionGroups.length - 1, s + 1)
                  )
                }
              >
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  submitting ? (
                    <CircularProgress size={14} />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                disabled={submitting || fields.length === 0}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            )}
          </Stack>
        ) : (
          <Button
            type="submit"
            variant="contained"
            startIcon={
              submitting ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
            disabled={submitting || fields.length === 0}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        )}
      </Stack>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || 'info'}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}
